import React, { useEffect, useState } from "react"
import { graphql } from "gatsby"
import Img from "gatsby-image/withIEPolyfill"

import { Header, FeaturedInsights, Footer, resolveComponent } from "@components/sections"
import {
  Meta,
  GridLines,
  ContentRow,
  ContentItem,
  ContentAside,
  PageHeading,
  Headline
} from "@shared"
import { CONSTANTS, URLS, CONTENT } from "@data"
import { logTool, css, featureFlag } from "@jsUtils"
import { getFriendlyPageName, titlesAndOrgs, getSafeDataMap } from "@jsUtils/contentful"

import Styles from "./bio.module.scss"

const { THEMES, COLORS, PAGE_NAMES } = CONSTANTS
const log = logTool()

const leadershipString = getFriendlyPageName(PAGE_NAMES.OUR_PEOPLE)

/**
 * Helper
 * @param {array} data - array of content objects for page builder
 */
const BioUnits = data => {
  log("BioUnits: ", data)
  return getSafeDataMap(data.data, module => {
    return (
      <ContentRow key={module.id || Date.now()} extraClass={Styles.rows}>
        <ContentItem gridArea="article">{resolveComponent(module)}</ContentItem>
      </ContentRow>
    )
  })
}

/**
 * Page Template
 * Render a Biography page with this template
 * Note this shells out calls to resolveComponent to determine what
 * additional components to import
 * @param {object} props - react props
 */
const Bio = props => {
  // eslint-disable-next-line no-unused-vars
  const { data } = props
  const { contentfulPerson, contentfulPageMeta } = data

  const [ready, setReady] = useState(false)

  log("Render Bio...", { contentfulPageMeta, contentfulPerson, Styles })

  // note: article is auto-generated. "articles" are selected
  const { headshot, name, title, titlesOrganizations, articles, bio } = contentfulPerson
  const bioTitle = (
    // eslint-disable-next-line react/no-danger
    <h3 dangerouslySetInnerHTML={{ __html: titlesAndOrgs(title, titlesOrganizations) }} />
  )

  useEffect(() => {
    setTimeout(() => {
      setReady(true)
    }, 200)
  }, [])

  return (
    <>
      <Meta base={contentfulPageMeta} page={contentfulPerson} />
      <Header theme={THEMES.TIN} pageColor={COLORS.LIGHT} />
      <main role="main">
        <article className={css(ready && Styles.ready)}>
          {/* 01 header area */}
          <ContentRow
            gridName="quartersGridRow"
            extraClass={css(Styles[`theme__${THEMES.TIN}`], Styles.introWrapper)}
          >
            <GridLines theme={THEMES.LIGHT} />
            <ContentAside
              linkTo={URLS.OUR_PEOPLE}
              extraClass={Styles.backLink}
              linkBack={!!featureFlag("link.backOurPeople")}
              targetAnchor={CONTENT.BIO.BACK_LINK_TARGET}
            />
            <PageHeading category={leadershipString} extraClass={Styles.label} />
          </ContentRow>

          {/* 02 headshot and title */}
          <ContentRow gridName="quartersGridRow" extraClass={Styles.titleWrapper}>
            <ContentItem gridArea="blockOne" extraClass={Styles.blockOneTitle}>
              {bioTitle}
            </ContentItem>
            <ContentItem gridArea="blockTwo" extraClass={Styles.blockTwoPhoto}>
              {headshot && <Img fluid={headshot.fluid} />}
            </ContentItem>
            <ContentItem gridArea="blockThree" extraClass={Styles.blockThreeName}>
              <Headline size={1} className={Styles.nameHeader}>
                {name}
              </Headline>
            </ContentItem>
          </ContentRow>

          {/* 03 Bio body */}
          <BioUnits data={bio} />
        </article>
      </main>
      <FeaturedInsights data={articles} />
      <Footer />
    </>
  )
}

export default Bio

export const pageQuery = graphql`
  query($slug: String!) {
    contentfulPageMeta(id: { eq: "423babd8-0278-5b2e-85f6-77fb7ee55bee" }) {
      ...GlobalMetaData
    }
    contentfulPerson(slug: { eq: $slug }) {
      slug
      name
      title
      pageMeta {
        ...PageMetaData
      }
      titlesOrganizations {
        childMarkdownRemark {
          rawMarkdownBody
          html
        }
      }
      headshot {
        id
        title
        fluid(quality: 100, maxWidth: 1920) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      bio {
        ... on Node {
          ... on ContentfulCopy {
            id
            content {
              childMarkdownRemark {
                html
              }
            }
          }
          ... on ContentfulQuote {
            id
            content {
              content
            }
          }
        }
      }
      articles {
        ... on ContentfulArticle {
          id
          slug
          title
          date
          category
          source
          thumbnail {
            title
            fluid(quality: 100, maxWidth: 1920) {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
        }
      }
    }
  }
`
